import React, { useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

function statusIcon(score) {
  if (score >= 80) return <CheckCircle2 className="w-3.5 h-3.5 text-bm-green" />;
  if (score >= 65) return <AlertCircle className="w-3.5 h-3.5 text-bm-amber" />;
  return <XCircle className="w-3.5 h-3.5 text-bm-danger" />;
}

function scoreColor(score) {
  if (score >= 80) return "text-bm-green";
  if (score >= 65) return "text-bm-amber";
  return "text-bm-danger";
}

// Flatten tree into rows with depth, then render iteratively to avoid recursive JSX
function flatten(root, depth = 0, path = "root", rows = []) {
  rows.push({ ...root, depth, path });
  if (root.children) {
    root.children.forEach((c, i) => flatten(c, depth + 1, `${path}-${i}`, rows));
  }
  return rows;
}

export default function ValidationTree({ tree }) {
  const treeLabel = {
    category: "Category Validation",
    behaviour: "Behaviour Validation",
    product: "Product Validation",
    localization: "India Localization",
    commercial: "Commercial Viability",
  };

  const rows = flatten(tree.root);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggle = (path) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // A row is visible if none of its ancestor paths are collapsed
  const isVisible = (path) => {
    const parts = path.split("-");
    for (let i = 1; i < parts.length; i++) {
      const ancestor = parts.slice(0, i).join("-");
      if (collapsed.has(ancestor)) return false;
    }
    return true;
  };

  return (
    <div className="bm-card p-3" data-testid={`validation-tree-${tree.tree_type}`}>
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
        <span className="label-mono text-bm-cyan">{treeLabel[tree.tree_type] || tree.tree_type}</span>
        <span className={`font-mono text-sm ${scoreColor(tree.overall_score)}`}>{tree.overall_score}%</span>
      </div>

      <div className="font-mono text-xs space-y-0.5">
        {rows.filter((r) => isVisible(r.path)).map((row) => {
          const hasChildren = row.children && row.children.length > 0;
          const isOpen = !collapsed.has(row.path);
          return (
            <div key={row.path} style={{ paddingLeft: `${row.depth * 14}px` }}>
              <button
                onClick={() => hasChildren && toggle(row.path)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 hover:bg-white/[0.03] text-left border-l-2 ${
                  hasChildren ? "border-bm-cyan/40" : "border-bm-amber/30"
                }`}
                data-testid={`tree-node-${row.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {hasChildren ? (
                  isOpen ? <ChevronDown className="w-3 h-3 text-bm-dim" /> : <ChevronRight className="w-3 h-3 text-bm-dim" />
                ) : <span className="w-3" />}
                {statusIcon(row.score)}
                <span className="text-white flex-1">{row.label}</span>
                <span className={scoreColor(row.score)}>{row.score}</span>
              </button>
              {isOpen && row.evidence && row.evidence.length > 0 && (
                <div className="border-l border-white/5 ml-3 pl-3 mt-0.5">
                  {row.evidence.map((e, i) => (
                    <div key={`${e}-${i}`} className="text-[11px] text-bm-muted py-0.5 leading-relaxed">— {e}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
